import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { eur } from "@/lib/format";
import { useT } from "@/i18n";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, AlertTriangle, XCircle, Video, Check, X, Loader2 } from "lucide-react";

const SCOPES = ["upcoming", "cancellation_pending", "completed", "cancelled", "all"];
const STATUS_CLS = {
  requested: "bg-blue-100 text-blue-700", confirmed: "bg-blue-100 text-blue-700", paid: "bg-blue-100 text-blue-700", pending: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
  pending_cancellation: "bg-amber-100 text-amber-700",
  cancelled_refunded: "bg-rose-100 text-rose-700", cancelled_no_refund: "bg-rose-100 text-rose-700", cancelled_refund_pending: "bg-rose-100 text-rose-700", declined: "bg-rose-100 text-rose-700",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}
function initials(n) { return (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase(); }

export function BookingsListTab() {
  const { user } = useAuth();
  const t = useT();
  const [kpis, setKpis] = useState(null);
  const [scope, setScope] = useState("upcoming");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const loadKpis = useCallback(() => {
    api.get("/coach/bookings/overview").then(({ data }) => setKpis(data?.kpis || {})).catch(() => setKpis({}));
  }, []);
  useEffect(() => { loadKpis(); }, [loadKpis]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/coach/bookings/list?scope=${scope}`); setRows(data?.bookings || []); }
    catch (err) { toast.error(apiError(err, t("bk.load_failed"))); }
    finally { setLoading(false); }
  }, [scope, t]);
  useEffect(() => { loadList(); }, [loadList]);

  const respond = async (b, approve) => {
    setBusyId(b.id);
    try {
      await api.post(`/coach/offerings/bookings/${b.id}/respond-cancel`, { approve });
      toast.success(approve ? t("bk.approved_toast") : t("bk.declined_toast"));
      await loadList();
      loadKpis();
    } catch (err) { toast.error(apiError(err, t("bk.action_failed"))); }
    finally { setBusyId(null); }
  };

  const k = kpis || {};
  return (
    <div data-testid="bk-bookings-tab">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("bk.kpi_upcoming")} value={k.upcoming ?? 0} icon={CalendarClock} testId="bk-kpi-upcoming" />
        <StatCard label={t("bk.kpi_completed")} value={k.completed ?? 0} icon={CheckCircle2} testId="bk-kpi-completed" />
        <StatCard label={t("bk.kpi_pending")} value={k.cancellation_pending ?? 0} icon={AlertTriangle} testId="bk-kpi-pending" />
        <StatCard label={t("bk.kpi_gross")} value={eur(k.gross_cents ?? 0)} icon={CheckCircle2} testId="bk-kpi-gross" />
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {SCOPES.map((s) => (
          <button key={s} type="button" onClick={() => setScope(s)} data-testid={`bk-scope-${s}`}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${scope === s ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
            {t(`bk.scope_${s}`)}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><CalendarClock className="h-6 w-6" /></div>
            <p className="mt-4 font-display text-lg text-foreground">{t("bk.empty_title")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("bk.empty_sub")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((b) => {
              const cls = STATUS_CLS[b.status] || "bg-secondary text-muted-foreground";
              return (
                <div key={b.id} data-testid={`booking-row-${b.id}`} className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      {b.client?.photo && <AvatarImage src={b.client.photo} alt={b.client?.name} />}
                      <AvatarFallback>{initials(b.client?.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-foreground">{b.client?.name || t("bk.client")}</p>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{t(`bk.status_${b.status}`)}</span>
                        {b.is_online && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground"><Video className="h-3 w-3" /> {t("bk.online")}</span>}
                      </div>
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">{b.offering?.title || t("bk.session")}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{fmtWhen(b.scheduled_at || b.created_at)}</p>
                      {b.cancel_reason && <p className="mt-1 text-xs italic text-muted-foreground">{t("bk.reason")}: {b.cancel_reason}</p>}
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-display text-lg font-semibold text-foreground">{eur(b.amount_cents || 0)}</p>
                      {b.status === "pending_cancellation" && b.policy_refund_pct != null && (
                        <p className="text-xs text-muted-foreground">{t("bk.refund_per_policy", { pct: Math.round(b.policy_refund_pct || 0) })}</p>
                      )}
                    </div>
                  </div>

                  {b.status === "pending_cancellation" && (
                    <div className="mt-3 flex gap-2 border-t border-border pt-3">
                      <Button size="sm" className="gap-1.5 rounded-full" disabled={busyId === b.id} onClick={() => respond(b, true)} data-testid={`booking-approve-${b.id}`}>
                        {busyId === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("bk.approve_refund")}
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busyId === b.id} onClick={() => respond(b, false)} data-testid={`booking-decline-${b.id}`}>
                        <X className="h-4 w-4" /> {t("bk.decline")}
                      </Button>
                    </div>
                  )}

                  {(b.status === "cancelled_refunded" || b.status === "cancelled_refund_pending") && b.refund_amount_cents != null && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><XCircle className="h-3.5 w-3.5" /> {t("bk.refunded", { amount: eur(b.refund_amount_cents || 0), pct: Math.round(b.refund_pct_applied || 0) })}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
